import { apiClient } from './apiClient';

import type { ApiResponse } from '@/types/auth.types';
import type { BoardData, BoardQueryParams } from '@/types/board.types';
import type { Comment } from '@/types/comment.types';
import type { CreateShareParams, ReviewInfo, ShareToken } from '@/types/submission.types';

interface CommentListData {
  items: Comment[];
}

interface CreateCommentParams {
  applicationId: string;
  text: string;
  visibility?: 'internal' | 'ops';
  timestampSec?: number;
}

interface CreateReviewParams {
  applicationId: string;
  decision: 'YES' | 'NO' | 'WAITLIST';
  reasonCode?: string;
  notes?: string;
}

interface AssignReviewerParams {
  applicationId: string;
  reviewerUserId: string;
  dueAt?: string;
}

interface AssignmentData {
  assignmentId: string;
  applicationId: string;
  reviewerUserId: string;
  reviewerFirstName: string;
  reviewerLastName: string;
  assignedAt: string;
  dueAt?: string;
}

export async function getBoard(params: BoardQueryParams): Promise<BoardData> {
  const searchParams = new URLSearchParams();

  if (params.q) searchParams.set('q', params.q);
  if (params.roles) searchParams.set('roles', params.roles);
  if (params.sites) searchParams.set('sites', params.sites);
  if (params.jobPostingId) searchParams.set('jobPostingId', params.jobPostingId);
  if (params.assignedTo) searchParams.set('assignedTo', params.assignedTo);
  if (params.from) searchParams.set('from', params.from);
  if (params.to) searchParams.set('to', params.to);

  const query = searchParams.toString();
  const url = `/reviews/board${query ? `?${query}` : ''}`;

  const response = await apiClient.get<ApiResponse<BoardData>>(url);
  return response.data;
}

export async function getComments(applicationId: string): Promise<Comment[]> {
  const response = await apiClient.get<ApiResponse<CommentListData>>(`/reviews/comments/${applicationId}`);
  return response.data.items;
}

export async function createComment(params: CreateCommentParams): Promise<Comment> {
  const response = await apiClient.post<ApiResponse<Comment>>('/reviews/comments', params);
  return response.data;
}

export async function deleteComment(commentId: string): Promise<Comment> {
  const response = await apiClient.delete<ApiResponse<Comment>>(`/reviews/comments/${commentId}`);
  return response.data;
}

export async function assignReviewer(params: AssignReviewerParams): Promise<AssignmentData> {
  const response = await apiClient.put<ApiResponse<AssignmentData>>('/reviews/assignments', params);
  return response.data;
}

export async function getReviews(applicationId: string): Promise<ReviewInfo[]> {
  const response = await apiClient.get<ApiResponse<ReviewInfo[]>>(`/reviews/history/${applicationId}`);
  return response.data;
}

export async function createReview(params: CreateReviewParams): Promise<ReviewInfo> {
  const response = await apiClient.post<ApiResponse<ReviewInfo>>('/reviews', params);
  return response.data;
}

interface ShareTokenListData {
  items: ShareToken[];
}

export async function listShareTokens(applicationId: string): Promise<ShareToken[]> {
  const response = await apiClient.get<ApiResponse<ShareTokenListData>>(
    `/reviews/shares?applicationId=${applicationId}`,
  );
  return response.data.items;
}

export async function createShareToken(params: CreateShareParams): Promise<ShareToken> {
  const response = await apiClient.post<ApiResponse<ShareToken>>('/reviews/shares', params);
  return response.data;
}

export async function revokeShareToken(shareTokenId: string): Promise<ShareToken> {
  const response = await apiClient.delete<ApiResponse<ShareToken>>(`/reviews/shares/${shareTokenId}`);
  return response.data;
}
